import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, Form, Input, Modal, Select, Space, Typography, message } from 'antd';
import { TIPOS_SOLICITUD_AUTOSERVICIO, crearMiSolicitud } from 'api/me';
import { TIPOS_SOLICITUD_ETIQUETAS } from './misDatosDiccionario';
import React, { useState } from 'react';

const { Text } = Typography;
const { TextArea } = Input;

/**
 * Los tres derechos que no se ejecutan solos: crean una solicitud que alguien revisa.
 *
 * Va en Modal y no en Drawer siguiendo a las páginas vecinas (Administradores.jsx
 * abre su form en Modal): es un form corto de dos campos.
 *
 * Las opciones se derivan de TIPOS_SOLICITUD_AUTOSERVICIO —la lista que el backend
 * acepta— y no de una lista escrita a mano acá, para que no se desincronicen. Si
 * el backend agregara un tipo que esta pantalla no sabe nombrar, se omite en vez
 * de mostrar el string crudo.
 */
const SolicitudModal = ({ open, onClose, onCreada }) => {
  const [form] = Form.useForm();
  const [enviando, setEnviando] = useState(false);
  const tipo = Form.useWatch('tipo', form);

  const opciones = TIPOS_SOLICITUD_AUTOSERVICIO.filter(
    (valor) => TIPOS_SOLICITUD_ETIQUETAS[valor]
  ).map((valor) => ({ value: valor, label: TIPOS_SOLICITUD_ETIQUETAS[valor].titulo }));

  const seleccionado = tipo ? TIPOS_SOLICITUD_ETIQUETAS[tipo] : null;

  const cerrar = () => {
    form.resetFields();
    onClose();
  };

  const enviar = async (valores) => {
    setEnviando(true);
    try {
      const solicitud = await crearMiSolicitud({
        tipo: valores.tipo,
        // El detalle es opcional: si va vacío no mandamos la clave en blanco.
        ...(valores.detalle?.trim() ? { detalle: valores.detalle.trim() } : {}),
      });
      message.success(
        'Recibimos tu solicitud. Una persona del equipo la va a revisar y te responderá.'
      );
      form.resetFields();
      onCreada?.(solicitud);
      onClose();
    } catch (error) {
      const detalle = error?.response?.data?.message;
      message.error(detalle || 'No pudimos enviar tu solicitud. Inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={cerrar}
      onOk={() => form.submit()}
      okText="Enviar solicitud"
      cancelText="Cancelar"
      confirmLoading={enviando}
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span>Pedir algo sobre tus datos</span>
        </Space>
      }
      width={560}
      destroyOnHidden
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Estas tres peticiones no se aplican al instante: alguien del equipo revisa primero si
        podemos hacerlas, porque hay registros de trabajos que la ley nos obliga a conservar.
      </Text>

      <Form form={form} layout="vertical" requiredMark={false} onFinish={enviar}>
        <Form.Item
          name="tipo"
          label="¿Qué necesitas?"
          rules={[{ required: true, message: 'Elige qué necesitas.' }]}
        >
          <Select placeholder="Elige una opción" options={opciones} />
        </Form.Item>

        {seleccionado && (
          <Alert
            type={seleccionado.irreversible ? 'warning' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            message={seleccionado.irreversible ? 'Esto no se puede deshacer' : undefined}
            description={
              seleccionado.irreversible
                ? `${seleccionado.detalle} Lo que se borre no se puede recuperar después.`
                : seleccionado.detalle
            }
          />
        )}

        <Form.Item
          name="detalle"
          label="Cuéntanos más (opcional)"
          extra="Si nos explicas tu caso, podemos resolverlo más rápido."
        >
          <TextArea
            rows={4}
            maxLength={1000}
            showCount
            placeholder="Escribe aquí tu explicación…"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SolicitudModal;
